import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Account,
  AccountStats,
  getAccountStatus,
  getAccountStatusLabel,
  getAccountStatusColor
} from './core/_models'
import { getAccounts, getAccountStats, createAccount, CreateAccountData } from './core/_requests'
import moment from 'moment'
import 'moment/locale/he'
moment.locale('he')

// Light theme colors matching admin-tickets.scss
const colors = {
  bgPrimary: '#f5f8fa',
  bgSecondary: '#ffffff',
  bgTertiary: '#e8ecef',
  bgHover: '#e1e5ea',
  textPrimary: '#1e1e2d',
  textSecondary: '#5e6278',
  textMuted: '#a1a5b7',
  accentPrimary: '#3699ff',
  accentSuccess: '#1bc5bd',
  accentWarning: '#ffa800',
  accentDanger: '#f64e60',
  borderColor: 'rgba(0, 0, 0, 0.08)',
}

const AccountsPage: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const [stats, setStats] = useState<AccountStats | null>(null)
  const [accounts, setAccounts] = useState<Account[]>([])
  const [loading, setLoading] = useState(true)
  const [hoveredCard, setHoveredCard] = useState<number | null>(null)
  const [hoveredRow, setHoveredRow] = useState<string | null>(null)

  const statusFilter = searchParams.get('status') ?? 'active'
  const marketplaceFilter = searchParams.get('marketplace') || ''
  const searchQuery = searchParams.get('search') || ''
  const [localSearch, setLocalSearch] = useState(searchQuery)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalAccounts, setTotalAccounts] = useState(0)
  const [searchFocused, setSearchFocused] = useState(false)

  // Create account modal state
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [createLoading, setCreateLoading] = useState(false)
  const [createFormData, setCreateFormData] = useState<CreateAccountData>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    company_name: '',
    trial_period_days: 14,
    monthly_payment_amount: 0,
    marketplace_enabled: false
  })

  useEffect(() => {
    loadData()
  }, [statusFilter, marketplaceFilter, searchQuery, currentPage])

  const loadData = async () => {
    setLoading(true)
    try {
      const params: any = {
        page: currentPage,
        limit: 25,
        sort: '-created_at'
      }

      if (searchQuery) params.search = searchQuery
      if (statusFilter === 'active') params.is_active = 'true'
      if (statusFilter === 'inactive') params.is_active = 'false'
      if (statusFilter === 'trial') params.trial_status = 'active'
      if (statusFilter === 'expired') params.trial_status = 'expired'
      if (marketplaceFilter === 'true') params.has_marketplace = 'true'
      if (marketplaceFilter === 'false') params.has_marketplace = 'false'

      const [statsRes, accountsRes] = await Promise.all([
        getAccountStats(),
        getAccounts(params)
      ])

      setStats(statsRes.data)
      setAccounts(accountsRes.data.accounts)
      setTotalPages(accountsRes.data.pagination.pages)
      setTotalAccounts(accountsRes.data.pagination.total)
    } catch (error) {
      console.error('Failed to load accounts:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    const newParams = new URLSearchParams(searchParams)
    if (localSearch) {
      newParams.set('search', localSearch)
    } else {
      newParams.delete('search')
    }
    setSearchParams(newParams)
    setCurrentPage(1)
  }

  const setFilter = (key: string, value: string) => {
    const newParams = new URLSearchParams()
    if (value) {
      newParams.set(key, value)
    }
    setSearchParams(newParams)
    setCurrentPage(1)
  }

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!createFormData.email || !createFormData.password) {
      alert('אימייל וסיסמה הם שדות חובה')
      return
    }

    setCreateLoading(true)
    try {
      const result = await createAccount(createFormData)
      if (result.data.success) {
        alert('החשבון נוצר בהצלחה!')
        setShowCreateModal(false)
        setCreateFormData({
          first_name: '',
          last_name: '',
          email: '',
          phone: '',
          password: '',
          company_name: '',
          trial_period_days: 14,
          monthly_payment_amount: 0,
          marketplace_enabled: false
        })
        loadData()
        // Navigate to the new account
        navigate(`/admin/accounts/${result.data.account._id}`)
      }
    } catch (error: any) {
      console.error('Failed to create account:', error)
      const message = error.response?.data?.error || 'שגיאה ביצירת החשבון'
      alert(message)
    } finally {
      setCreateLoading(false)
    }
  }

  const getAccountName = (account: Account) => {
    const name = `${account.first_name || ''} ${account.last_name || ''}`.trim()
    return name || account.email || 'Unknown'
  }

  const getTrialDaysLeft = (account: Account) => {
    if (!account.trial_period_end) return null
    const end = moment(account.trial_period_end)
    const now = moment()
    const days = end.diff(now, 'days')
    if (days < 0) return { text: 'פג', color: colors.accentDanger }
    if (days === 0) return { text: 'היום', color: colors.accentWarning }
    if (days <= 7) return { text: `${days} ימים`, color: colors.accentWarning }
    return { text: `${days} ימים`, color: colors.accentSuccess }
  }

  const statsData = [
    {
      label: 'סה"כ חשבונות',
      value: stats?.totalAccounts || 0,
      icon: 'bi-people-fill',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      filter: ''
    },
    {
      label: 'פעילים',
      value: stats?.activeAccounts || 0,
      icon: 'bi-lightning-charge-fill',
      gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
      filter: 'active'
    },
    {
      label: 'תקופת ניסיון',
      value: stats?.trialAccounts || 0,
      icon: 'bi-hourglass-split',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      filter: 'trial'
    },
  ]

  const avatarGradients = [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  ]

  const getStatusStyle = (status: string) => {
    const styles: Record<string, { bg: string; color: string; border: string }> = {
      success: { bg: 'rgba(27, 197, 189, 0.15)', color: '#1bc5bd', border: 'rgba(27, 197, 189, 0.3)' },
      info: { bg: 'rgba(54, 153, 255, 0.15)', color: '#3699ff', border: 'rgba(54, 153, 255, 0.3)' },
      warning: { bg: 'rgba(255, 168, 0, 0.15)', color: '#ffa800', border: 'rgba(255, 168, 0, 0.3)' },
      danger: { bg: 'rgba(246, 78, 96, 0.15)', color: '#f64e60', border: 'rgba(246, 78, 96, 0.3)' },
      secondary: { bg: 'rgba(126, 130, 153, 0.15)', color: '#7e8299', border: 'rgba(126, 130, 153, 0.3)' },
    }
    return styles[status] || styles.secondary
  }

  return (
    <div style={{ padding: 0 }}>
      {/* Page Header */}
      <div style={{ marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{
            fontSize: '24px',
            fontWeight: 600,
            color: colors.textPrimary,
            margin: 0,
          }}>
            ניהול חשבונות
          </h1>
          <p style={{ color: colors.textSecondary, marginTop: '8px', fontSize: '14px' }}>
            צפייה וניהול כל החשבונות במערכת
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '12px 20px',
            background: colors.accentPrimary,
            border: 'none',
            borderRadius: '8px',
            color: '#fff',
            fontSize: '14px',
            fontWeight: 500,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
        >
          <i className="bi bi-plus-lg" style={{ fontSize: '16px' }}></i>
          חשבון חדש
        </button>
      </div>

      {/* Stats Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '16px',
        marginBottom: '24px',
      }}>
        {statsData.map((stat, i) => (
          <div
            key={i}
            onClick={() => stat.filter === 'marketplace' ? setFilter('marketplace', 'true') : setFilter('status', stat.filter)}
            onMouseEnter={() => setHoveredCard(i)}
            onMouseLeave={() => setHoveredCard(null)}
            style={{
              background: colors.bgTertiary,
              borderRadius: '12px',
              padding: '20px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              transform: hoveredCard === i ? 'translateY(-2px)' : 'none',
              border: `1px solid ${hoveredCard === i ? colors.borderColor : 'transparent'}`,
            }}
          >
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: stat.gradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '12px',
            }}>
              <i className={`bi ${stat.icon}`} style={{
                fontSize: '20px',
                color: '#fff',
              }}></i>
            </div>
            <div style={{
              fontSize: '28px',
              fontWeight: 700,
              color: colors.textPrimary,
              marginBottom: '4px',
            }}>
              {stat.value.toLocaleString()}
            </div>
            <div style={{
              fontSize: '13px',
              color: colors.textSecondary,
            }}>
              {stat.label}
            </div>
          </div>
        ))}
      </div>

      {/* Search & Filters Bar */}
      <div style={{
        display: 'flex',
        gap: '16px',
        marginBottom: '20px',
        alignItems: 'center',
        background: colors.bgTertiary,
        padding: '12px 16px',
        borderRadius: '8px',
      }}>
        <form onSubmit={handleSearch} style={{ flex: 1, position: 'relative' }}>
          <i className="bi bi-search" style={{
            position: 'absolute',
            right: '14px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: colors.textMuted,
            fontSize: '16px',
          }}></i>
          <input
            type="text"
            placeholder="חיפוש לפי שם, אימייל, טלפון..."
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            style={{
              width: '100%',
              padding: '12px 44px 12px 16px',
              background: searchFocused ? colors.bgSecondary : 'transparent',
              border: 'none',
              borderRadius: '6px',
              color: colors.textPrimary,
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s ease',
            }}
          />
        </form>

        <div style={{ display: 'flex', gap: '12px' }}>
          <select
            value={statusFilter}
            onChange={(e) => setFilter('status', e.target.value)}
            style={{
              padding: '12px 16px',
              borderRadius: '6px',
              border: `1px solid ${colors.borderColor}`,
              background: colors.bgSecondary,
              color: colors.textPrimary,
              fontSize: '13px',
              outline: 'none',
              cursor: 'pointer',
              minWidth: '140px',
            }}
          >
            <option value="active">פעילים</option>
            <option value="">כל הסטטוסים</option>
            <option value="inactive">לא פעילים</option>
            <option value="trial">תקופת ניסיון</option>
            <option value="expired">פג תוקף</option>
          </select>

        </div>
      </div>

      {/* Table */}
      <div style={{
        background: colors.bgTertiary,
        borderRadius: '12px',
        overflow: 'hidden',
      }}>
        {/* Table Header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '2.5fr 2fr 1fr 1.2fr 1fr',
          padding: '16px 24px',
          borderBottom: `1px solid ${colors.borderColor}`,
        }}>
          {['חשבון', 'אימייל', 'פרופילים', 'סטטוס', 'תוקף'].map((header, i) => (
            <div key={i} style={{
              fontSize: '11px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              color: colors.textMuted,
            }}>
              {header}
            </div>
          ))}
        </div>

        {/* Table Body */}
        {loading ? (
          <div style={{
            padding: '60px',
            textAlign: 'center',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: `3px solid ${colors.bgHover}`,
              borderTopColor: colors.accentPrimary,
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
            }}></div>
          </div>
        ) : accounts.length === 0 ? (
          <div style={{
            padding: '60px',
            textAlign: 'center',
          }}>
            <i className="bi bi-inbox" style={{ fontSize: '48px', color: colors.textMuted, marginBottom: '16px', display: 'block' }}></i>
            <div style={{ fontSize: '18px', fontWeight: 600, color: colors.textPrimary, marginBottom: '8px' }}>לא נמצאו חשבונות</div>
            <div style={{ fontSize: '14px', color: colors.textSecondary }}>נסה לשנות את פרמטרי החיפוש</div>
          </div>
        ) : (
          <>
            {accounts.map((account, index) => {
              const statusColor = getAccountStatusColor(account)
              const statusStyle = getStatusStyle(statusColor)
              const trialInfo = getTrialDaysLeft(account)

              return (
                <div
                  key={account._id}
                  onClick={() => navigate(`/admin/accounts/${account._id}`)}
                  onMouseEnter={() => setHoveredRow(account._id)}
                  onMouseLeave={() => setHoveredRow(null)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '2.5fr 2fr 1fr 1.2fr 1fr',
                    padding: '16px 24px',
                    alignItems: 'center',
                    borderBottom: `1px solid rgba(0,0,0,0.05)`,
                    cursor: 'pointer',
                    background: hoveredRow === account._id ? colors.bgHover : 'transparent',
                    transition: 'background 0.15s ease',
                  }}
                >
                  {/* Account Info */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: avatarGradients[index % avatarGradients.length],
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 600,
                      fontSize: '14px',
                      color: '#fff',
                    }}>
                      {getAccountName(account).charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, color: colors.textPrimary, fontSize: '14px' }}>
                        {getAccountName(account)}
                      </div>
                      {account.company_name && (
                        <div style={{ fontSize: '12px', color: colors.textSecondary, marginTop: '2px' }}>
                          {account.company_name}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Email */}
                  <div style={{ color: colors.textSecondary, fontSize: '13px' }}>
                    {account.email}
                  </div>

                  {/* Profiles */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{
                      fontWeight: 600,
                      color: colors.textPrimary,
                      fontSize: '14px',
                    }}>
                      {account.profile_count || 0}
                    </span>
                    {account.active_profiles ? (
                      <span style={{
                        color: colors.accentSuccess,
                        fontSize: '11px',
                        background: 'rgba(27, 197, 189, 0.15)',
                        padding: '2px 6px',
                        borderRadius: '4px',
                      }}>
                        {account.active_profiles} פעילים
                      </span>
                    ) : null}
                  </div>

                  {/* Status */}
                  <div>
                    <span style={{
                      padding: '4px 10px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: 600,
                      background: statusStyle.bg,
                      color: statusStyle.color,
                    }}>
                      {getAccountStatusLabel(account)}
                    </span>
                  </div>

                  {/* Trial */}
                  <div>
                    {trialInfo ? (
                      <span style={{
                        fontSize: '13px',
                        fontWeight: 500,
                        color: trialInfo.color,
                      }}>
                        {trialInfo.text}
                      </span>
                    ) : (
                      <span style={{ color: colors.textMuted }}>—</span>
                    )}
                  </div>

                </div>
              )
            })}

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px',
                gap: '8px',
              }}>
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '6px',
                    border: 'none',
                    background: colors.bgTertiary,
                    color: currentPage === 1 ? colors.textMuted : colors.textSecondary,
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <i className="bi bi-chevron-right"></i>
                </button>

                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let page: number
                  if (totalPages <= 5) page = i + 1
                  else if (currentPage <= 3) page = i + 1
                  else if (currentPage >= totalPages - 2) page = totalPages - 4 + i
                  else page = currentPage - 2 + i

                  const isActive = currentPage === page

                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '6px',
                        border: 'none',
                        background: isActive ? colors.accentPrimary : colors.bgTertiary,
                        color: isActive ? '#fff' : colors.textSecondary,
                        cursor: 'pointer',
                        fontWeight: isActive ? 600 : 400,
                        transition: 'all 0.15s ease',
                      }}
                    >
                      {page}
                    </button>
                  )
                })}

                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '6px',
                    border: 'none',
                    background: colors.bgTertiary,
                    color: currentPage === totalPages ? colors.textMuted : colors.textSecondary,
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <i className="bi bi-chevron-left"></i>
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Account Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: colors.bgSecondary,
            borderRadius: '12px',
            padding: '28px',
            width: '100%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          }}>
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px',
            }}>
              <h2 style={{
                fontSize: '20px',
                fontWeight: 600,
                color: colors.textPrimary,
                margin: 0,
              }}>
                יצירת חשבון חדש
              </h2>
              <button
                onClick={() => setShowCreateModal(false)}
                style={{
                  background: colors.bgTertiary,
                  border: 'none',
                  borderRadius: '8px',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: colors.textSecondary,
                }}
              >
                <i className="bi bi-x-lg" style={{ fontSize: '16px' }}></i>
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleCreateAccount}>
              {/* Name Row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', color: colors.textMuted, fontSize: '11px', marginBottom: '6px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    שם פרטי
                  </label>
                  <input
                    type="text"
                    value={createFormData.first_name}
                    onChange={(e) => setCreateFormData({ ...createFormData, first_name: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      background: colors.bgPrimary,
                      border: `1px solid ${colors.borderColor}`,
                      borderRadius: '6px',
                      color: colors.textPrimary,
                      fontSize: '14px',
                      outline: 'none',
                    }}
                    placeholder="הכנס שם פרטי"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', color: colors.textMuted, fontSize: '11px', marginBottom: '6px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    שם משפחה
                  </label>
                  <input
                    type="text"
                    value={createFormData.last_name}
                    onChange={(e) => setCreateFormData({ ...createFormData, last_name: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      background: colors.bgPrimary,
                      border: `1px solid ${colors.borderColor}`,
                      borderRadius: '6px',
                      color: colors.textPrimary,
                      fontSize: '14px',
                      outline: 'none',
                    }}
                    placeholder="הכנס שם משפחה"
                  />
                </div>
              </div>

              {/* Email */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', color: colors.textMuted, fontSize: '11px', marginBottom: '6px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  אימייל <span style={{ color: colors.accentDanger }}>*</span>
                </label>
                <input
                  type="email"
                  required
                  value={createFormData.email}
                  onChange={(e) => setCreateFormData({ ...createFormData, email: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    background: colors.bgPrimary,
                    border: `1px solid ${colors.borderColor}`,
                    borderRadius: '6px',
                    color: colors.textPrimary,
                    fontSize: '14px',
                    outline: 'none',
                  }}
                  placeholder="example@email.com"
                />
              </div>

              {/* Password */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', color: colors.textMuted, fontSize: '11px', marginBottom: '6px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  סיסמה <span style={{ color: colors.accentDanger }}>*</span>
                </label>
                <input
                  type="password"
                  required
                  value={createFormData.password}
                  onChange={(e) => setCreateFormData({ ...createFormData, password: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    background: colors.bgPrimary,
                    border: `1px solid ${colors.borderColor}`,
                    borderRadius: '6px',
                    color: colors.textPrimary,
                    fontSize: '14px',
                    outline: 'none',
                  }}
                  placeholder="הכנס סיסמה"
                />
              </div>

              {/* Phone */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', color: colors.textMuted, fontSize: '11px', marginBottom: '6px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  טלפון
                </label>
                <input
                  type="tel"
                  value={createFormData.phone}
                  onChange={(e) => setCreateFormData({ ...createFormData, phone: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    background: colors.bgPrimary,
                    border: `1px solid ${colors.borderColor}`,
                    borderRadius: '6px',
                    color: colors.textPrimary,
                    fontSize: '14px',
                    outline: 'none',
                  }}
                  placeholder="050-0000000"
                />
              </div>

              {/* Company Name */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', color: colors.textMuted, fontSize: '11px', marginBottom: '6px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  שם חברה
                </label>
                <input
                  type="text"
                  value={createFormData.company_name}
                  onChange={(e) => setCreateFormData({ ...createFormData, company_name: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    background: colors.bgPrimary,
                    border: `1px solid ${colors.borderColor}`,
                    borderRadius: '6px',
                    color: colors.textPrimary,
                    fontSize: '14px',
                    outline: 'none',
                  }}
                  placeholder="הכנס שם חברה"
                />
              </div>

              {/* Trial Period & Payment Row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', color: colors.textMuted, fontSize: '11px', marginBottom: '6px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    תקופת ניסיון (ימים)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={createFormData.trial_period_days}
                    onChange={(e) => setCreateFormData({ ...createFormData, trial_period_days: parseInt(e.target.value) || 0 })}
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      background: colors.bgPrimary,
                      border: `1px solid ${colors.borderColor}`,
                      borderRadius: '6px',
                      color: colors.textPrimary,
                      fontSize: '14px',
                      outline: 'none',
                    }}
                    placeholder="14"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', color: colors.textMuted, fontSize: '11px', marginBottom: '6px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    תשלום חודשי (₪)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={createFormData.monthly_payment_amount}
                    onChange={(e) => setCreateFormData({ ...createFormData, monthly_payment_amount: parseInt(e.target.value) || 0 })}
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      background: colors.bgPrimary,
                      border: `1px solid ${colors.borderColor}`,
                      borderRadius: '6px',
                      color: colors.textPrimary,
                      fontSize: '14px',
                      outline: 'none',
                    }}
                    placeholder="0"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  style={{
                    padding: '12px 24px',
                    background: colors.bgTertiary,
                    border: 'none',
                    borderRadius: '6px',
                    color: colors.textPrimary,
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                >
                  ביטול
                </button>
                <button
                  type="submit"
                  disabled={createLoading}
                  style={{
                    padding: '12px 24px',
                    background: colors.accentPrimary,
                    border: 'none',
                    borderRadius: '6px',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: createLoading ? 'not-allowed' : 'pointer',
                    opacity: createLoading ? 0.7 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  {createLoading ? (
                    <>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid rgba(255,255,255,0.3)',
                        borderTopColor: '#fff',
                        borderRadius: '50%',
                        animation: 'spin 0.8s linear infinite',
                      }}></div>
                      יוצר...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-plus-lg"></i>
                      צור חשבון
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        select option {
          background: ${colors.bgSecondary};
          color: ${colors.textPrimary};
        }
      `}</style>
    </div>
  )
}

export default AccountsPage
